import React, { useState } from 'react';
import { Warehouse, Truck, Train, Plane, BarChart3, Network } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { services } from '../data/mock';

export const Services = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Warehousing', 'Logistics', 'SCM Consultancy'];

  const serviceIcons = {
    'Multi-Client Warehousing': Warehouse,
    'Dedicated Warehousing': Warehouse,
    'In-house Inventory Management': BarChart3,
    'Rail Logistics': Train,
    'Full Truck / Part Loads': Truck,
    'Air Freight': Plane,
    'HNS & ETAS Study': Network,
    'Logistics Cost Analysis': BarChart3,
    'Logistics Network Design': Network
  };

  const filteredServices = activeCategory === 'All' 
    ? services 
    : services.filter(service => service.category === activeCategory);

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1695222833131-54ee679ae8e5"
            alt="Logistics"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Complete logistics solutions tailored to your business needs — from warehousing to strategic consulting
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  activeCategory === category
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {filteredServices.map((service) => {
              const Icon = serviceIcons[service.title];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-2 border-slate-100 hover:border-orange-200 bg-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-100 to-cyan-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="text-orange-600" size={32} />
                    </div>
                    <div className="inline-block mb-3">
                      <span className="text-xs font-medium text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                        {service.category}
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <div className="border-t pt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">Key Features:</p>
                      <ul className="space-y-2">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="text-orange-500 mt-1">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services Accordion */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">Service Categories</h2>
              <p className="text-slate-600">Explore our comprehensive service offerings in detail</p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="warehousing" className="border-2 border-slate-200 rounded-xl px-6 bg-white">
                <AccordionTrigger className="text-xl font-bold text-slate-900 hover:text-orange-600 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Warehouse className="text-orange-600" size={24} />
                    </div>
                    Warehousing Solutions
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <div className="pl-16 space-y-4">
                    <p className="text-slate-600 leading-relaxed">
                      Our warehousing solutions span across 27+ strategically located facilities in 19+ states. We offer flexible, scalable storage options with advanced technology integration.
                    </p>
                    <ul className="space-y-2">
                      <li className="text-slate-700">• Multi-Client Warehousing with shared resources</li>
                      <li className="text-slate-700">• Dedicated Warehousing for exclusive operations</li>
                      <li className="text-slate-700">• In-house Inventory Management systems</li>
                      <li className="text-slate-700">• Climate-controlled storage options</li>
                      <li className="text-slate-700">• 24/7 security and monitoring</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="logistics" className="border-2 border-slate-200 rounded-xl px-6 bg-white">
                <AccordionTrigger className="text-xl font-bold text-slate-900 hover:text-orange-600 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Truck className="text-cyan-600" size={24} />
                    </div>
                    Transportation & Logistics
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <div className="pl-16 space-y-4">
                    <p className="text-slate-600 leading-relaxed">
                      Comprehensive multi-modal transportation solutions covering rail, road, and air freight across India with 67,000+ successful deliveries.
                    </p>
                    <ul className="space-y-2">
                      <li className="text-slate-700">• Rail Logistics with 80+ train cargo operations</li>
                      <li className="text-slate-700">• Full Truck Load (FTL) and Part Load (LTL) services</li>
                      <li className="text-slate-700">• Air Freight for time-sensitive cargo</li>
                      <li className="text-slate-700">• GPS-enabled real-time tracking</li>
                      <li className="text-slate-700">• Door-to-door delivery solutions</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="consulting" className="border-2 border-slate-200 rounded-xl px-6 bg-white">
                <AccordionTrigger className="text-xl font-bold text-slate-900 hover:text-orange-600 py-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Network className="text-green-600" size={24} />
                    </div>
                    Supply Chain Consultancy
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  <div className="pl-16 space-y-4">
                    <p className="text-slate-600 leading-relaxed">
                      Strategic supply chain consulting to optimize your logistics network, reduce costs, and improve operational efficiency.
                    </p>
                    <ul className="space-y-2">
                      <li className="text-slate-700">• Hub & Spoke Network Studies (HNS)</li>
                      <li className="text-slate-700">• Estimated Time of Arrival Studies (ETAS)</li>
                      <li className="text-slate-700">• Comprehensive Logistics Cost Analysis</li>
                      <li className="text-slate-700">• Network Design and Optimization</li>
                      <li className="text-slate-700">• Technology integration recommendations</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-4xl font-bold mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-xl mb-8 text-orange-50">
              Let's discuss how we can optimize your supply chain with our integrated logistics services.
            </p>
            <a href="/contact">
              <button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Get in Touch
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
